import { AgentsZapierFacade } from ".";
import { getImplementation } from "./implementations";
import {
  ConnectionId,
  NeedChoicesRequest,
  NeedChoicesSchema,
  ZapierFacadeError,
} from "./types";
import { prepareParamsForSend } from "./util";

/**
 * Normalize the action id so that downstream calls receive the value they
 * expect.
 */
function normalizeActionId(actionId?: string): string | undefined {
  // Remove the `ae:` prefix that appears on Custom Actions
  // see https://zapier.slack.com/archives/C061QRE874K/p1747142365196669
  if (actionId?.startsWith("ae:")) {
    return actionId.slice(3);
  }
  return actionId;
}

export async function getChoices(
  facade: AgentsZapierFacade,
  {
    selectedApi,
    prefill,
    actionId,
    inputFieldId,
    authenticationId,
    params: bodyParams,
    page,
  }: {
    selectedApi: string;
    prefill?: string;
    actionId?: string;
    inputFieldId?: string;
    authenticationId?: ConnectionId;
    params: Record<string, unknown>;
    page?: number;
  },
): Promise<{
  results: NeedChoicesSchema[];
  nextPage: number | null;
}> {
  let versionedSelectedApi = selectedApi;

  if (!versionedSelectedApi.includes("@")) {
    // getChoices requires a versioned selectedApi (e.g. 'SlackCLIAPI@1.3.0')
    // If a user passes an unversioned selectedApi (e.g. 'SlackCLIAPI'), we fetch the implementation to get the versioned selectedApi
    // FIXME(Nik, Vitor): Remove this call once team-edge API supports unversioned selectedApi param
    const implementation = await getImplementation(facade, {
      selectedApi: versionedSelectedApi,
    });
    versionedSelectedApi = implementation.selected_api;
  }

  const normalizedActionId = normalizeActionId(actionId);

  const needChoicesRequest: NeedChoicesRequest = {
    selected_api: versionedSelectedApi,
    prefill: prefill,
    action_id: normalizedActionId,
    input_field_id: inputFieldId,
    params: prepareParamsForSend(bodyParams),
    authentication_id: authenticationId,
    page: page ?? 0,
  };

  const { data, error, response } = await facade.api.POST(
    `/api/v4/implementations/choices/`,
    { body: needChoicesRequest },
  );

  if (error || !data.success) {
    throw new ZapierFacadeError(
      "/api/v4/implementations/choices/",
      "POST",
      needChoicesRequest,
      response,
      error,
    );
  }

  return {
    results: data.choices ?? [],
    nextPage: data.next_page ?? null,
  };
}

/**
 * Gets all choices for a need by iterating through pages until either:
 * 1. No new choices are returned (nextPage is null)
 * 2. The maximum number of pages is reached
 */
export async function getChoicesPaged(
  facade: AgentsZapierFacade,
  params: {
    selectedApi: string;
    authenticationId?: ConnectionId;
    params: Record<string, unknown>;
    prefill?: string;
    actionId?: string;
    inputFieldId?: string;
    maxPages?: number;
  },
): Promise<{
  results: NeedChoicesSchema[];
}> {
  const maxPages = params.maxPages ?? 30;
  const seenChoiceKeys = new Set<string>();
  const allChoices: NeedChoicesSchema[] = [];
  let page = 0;

  if (!params.selectedApi.includes("@")) {
    // getChoices requires a versioned selectedApi (e.g. 'SlackCLIAPI@1.3.0')
    // If a user passes an unversioned selectedApi (e.g. 'SlackCLIAPI'), we fetch the implementation to get the versioned selectedApi
    // FIXME(Nik, Vitor): Remove this call once team-edge API supports unversioned selectedApi param
    const implementation = await getImplementation(facade, {
      selectedApi: params.selectedApi,
    });
    params.selectedApi = implementation.selected_api;
  }

  while (page < maxPages) {
    let newChoices: NeedChoicesSchema[] = [];
    let nextPage: number | null = null;

    try {
      const result = await getChoices(facade, {
        ...params,
        page,
      });
      newChoices = result.results;
      nextPage = result.nextPage;
    } catch (err) {
      // If we already fetched at least one page successfully, we can treat
      // subsequent pagination errors as a signal that there are no more
      // valid pages. Common causes: 400s from upstream app validation
      // errors, and 500s from a known Zapier API bug on later pages.
      // In that scenario we return whatever we have gathered so far.
      // Only re-throw if this is the very first page.
      const isFirstPage = page === 0;
      const isFacadeError = err instanceof ZapierFacadeError;
      const status = isFacadeError ? err.response?.status : undefined;
      const isRecoverablePaginationError =
        isFacadeError && (status === 400 || status === 500);

      if (!isFirstPage && isRecoverablePaginationError) {
        console.warn(
          `getChoicesPaged: stopping pagination on page ${page} due to ${status} error; returning ${allChoices.length} choices gathered so far`,
        );
        break;
      }

      // Otherwise, propagate the error up the chain so callers can handle it
      throw err;
    }

    // Check if we got any new choices that we haven't seen before
    const newUniqueChoices = newChoices.filter((choice) => {
      if (!choice.key || seenChoiceKeys.has(choice.key)) {
        return false;
      }
      seenChoiceKeys.add(choice.key);
      return true;
    });

    // If we didn't get any new unique choices, we're done
    if (newUniqueChoices.length === 0) {
      break;
    }

    allChoices.push(...newUniqueChoices);

    // If there's no next page, we're done
    if (nextPage === null) {
      break;
    }

    page++;
  }

  return { results: allChoices };
}
